import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { runWorker, STAGE, type ChartData, type BeatstageBundleV1 } from '../worker-messages';

interface Metadata {
  title: string;
  artist: string;
  bpm: number;
  offsetMs: number;
  beatDivision: number;
  durationMs: number;
}

interface TimingPoint {
  time: number;
  bpm: number;
  meter: number;
  offsetMs: number;
}

interface Props {
  meta: Metadata;
  chart: ChartData;
  timing: TimingPoint[];
  /** Blob for the song audio. */
  audioBlob?: Blob;
  /** Optional background video blob. */
  videoBlob?: Blob;
}

export default function ExportPanel({ meta, chart, timing, audioBlob, videoBlob }: Props) {
  const [encoded, setEncoded] = useState<string>('');
  const [size, setSize] = useState<number>(0);
  const [chartHash, setChartHash] = useState<string>('');
  const [audioHash, setAudioHash] = useState<string>('');
  const [audioSize, setAudioSize] = useState<number>(0);
  const [audioEncoded, setAudioEncoded] = useState<string>('');
  const [audioMime, setAudioMime] = useState<string>('');
  const [videoHash, setVideoHash] = useState<string>('');
  const [videoSize, setVideoSize] = useState<number>(0);
  const [videoEncoded, setVideoEncoded] = useState<string>('');
  const [videoMime, setVideoMime] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    const encode = async () => {
      const bundle: BeatstageBundleV1 = {
        version: 1,
        mode: 'circle',
        meta: {
          ...meta,
          audio: audioHash
            ? { hash: audioHash, size: audioSize, mime: audioMime }
            : undefined,
          video: videoHash
            ? { hash: videoHash, size: videoSize, mime: videoMime }
            : undefined,
          stage: STAGE,
        },
        timing: { gridMs: timing.map((t) => t.time), segments: [] },
        chart,
        supportsMods: {
          easy: true,
          hardRock: true,
          doubleTime: true,
          halfTime: true,
          hidden: true,
          flashlight: true,
          noFail: true,
        },
        generator: 'export-panel',
        stats: {
          totalNotes: chart.clickPoints.length,
        },
        // Embed raw audio so the resulting bundle is self-contained
        audio: audioEncoded ? { data: audioEncoded } : undefined,
        video: videoEncoded ? { data: videoEncoded } : undefined,
      };
      const { encoded, hash, size } = await runWorker({ type: 'serialize', bundle });
      if (!cancelled) {
        setEncoded(encoded);
        setChartHash(hash);
        setSize(size);
      }
    };
    void encode();
    return () => {
      cancelled = true;
    };
  }, [
    meta,
    chart,
    timing,
    audioHash,
    audioSize,
    audioEncoded,
    audioMime,
    videoHash,
    videoSize,
    videoEncoded,
    videoMime,
  ]);

  useEffect(() => {
    const computeAudio = async () => {
      if (!audioBlob) {
        setAudioHash('');
        setAudioSize(0);
        setAudioEncoded('');
        setAudioMime('');
        return;
      }
      try {
        const original = await audioBlob.arrayBuffer();
        const size = original.byteLength;
        // Transfer separate copy for hashing to avoid neutering issues
        const hashBuf = original.slice(0);
        const [{ hash }] = await Promise.all([
          runWorker({ type: 'hash', buffer: hashBuf }, undefined, [hashBuf]),
        ]);
        setAudioMime(audioBlob.type || 'audio/mpeg');
        // Convert raw audio buffer to base64 so it can be embedded directly
        let binary = '';
        const bytes = new Uint8Array(original);
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        setAudioHash(hash);
        setAudioSize(size);
        setAudioEncoded(base64);
      } catch (err) {
        console.error(err);
        setAudioHash('');
        setAudioSize(0);
        setAudioEncoded('');
        setAudioMime('');
      }
    };
    void computeAudio();
  }, [audioBlob]);

  useEffect(() => {
    const computeVideo = async () => {
      if (!videoBlob) {
        setVideoHash('');
        setVideoSize(0);
        setVideoEncoded('');
        setVideoMime('');
        return;
      }
      try {
        const original = await videoBlob.arrayBuffer();
        const size = original.byteLength;
        const hashBuf = original.slice(0);
        const [{ hash }] = await Promise.all([
          runWorker({ type: 'hash', buffer: hashBuf }, undefined, [hashBuf]),
        ]);
        setVideoMime(videoBlob.type || 'video/webp');
        let binary = '';
        const bytes = new Uint8Array(original);
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        setVideoHash(hash);
        setVideoSize(size);
        setVideoEncoded(base64);
      } catch (err) {
        console.error(err);
        setVideoHash('');
        setVideoSize(0);
        setVideoEncoded('');
        setVideoMime('');
      }
    };
    void computeVideo();
  }, [videoBlob]);

  const download = () => {
    if (!encoded) return;
    const a = document.createElement('a');
    a.href = `data:application/octet-stream;base64,${encoded}`;
    a.download = `${meta.title || 'chart'}.beatstage`;
    a.click();
  };

  return (
    <div className="p-2 bg-white/90 text-black rounded shadow space-y-2">
      <div className="flex gap-2">
        <Button onClick={download} disabled={!encoded} size="sm">
          Download .beatstage
        </Button>
      </div>
      <div className="text-xs space-y-1">
        <div>Bundle size: {size} bytes</div>
        <div>Chart SHA-256: {chartHash}</div>
        <div>Audio SHA-256: {audioHash || 'N/A'}</div>
        <div>Video SHA-256: {videoHash || 'N/A'}</div>
      </div>
    </div>
  );
}

